/**
 * Tracking ref generation and normalization.
 *
 * Legacy refs (e.g. ELKF8DF6292) are 8 hex characters after the prefix,
 * generated client-side from a random UUID. Hex is full of handwriting
 * lookalikes (0/O, and — combined with the warehouse's own shorthand — B/E),
 * which is exactly what caused the transcription errors the warehouse
 * import's fuzzy-matcher exists to paper over.
 *
 * New refs use Crockford's Base32 alphabet, which drops the four most
 * confusable letters (I, L, O, U) and defines a standard normalization for
 * anyone who types them anyway, plus a check character that catches a
 * mistyped or transposed character before it ever reaches the database. Ref
 * shape: <PREFIX> + 6 body characters + 1 check character (7-char suffix,
 * vs. the legacy 8-char hex suffix — the differing length is how the two
 * formats are told apart).
 *
 * The check character is a Luhn mod-32 digit drawn from the same safe
 * alphabet as the body. The first batch of new-format refs used Crockford's
 * mod-37 check symbol instead, whose alphabet adds *, ~, $, = and U: those
 * get percent-encoded in URLs, are easy to drop when handwritten, and U is
 * one of the letters the body alphabet excludes. Refs issued under that
 * scheme (e.g. ELKREA93N$) are still accepted by the validator.
 */

const SAFE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // Crockford Base32
// Crockford's check-symbol alphabet: the 32 body symbols plus 5 more for a
// mod-37 check. Only used to validate refs issued before the Luhn switch.
const LEGACY_CHECK_ALPHABET = `${SAFE_ALPHABET}*~$=U`;

const BODY_LENGTH = 6;

function crockfordValue(char: string): number {
  const index = SAFE_ALPHABET.indexOf(char);
  if (index < 0) {
    throw new Error(`Invalid Crockford Base32 character: ${char}`);
  }
  return index;
}

/** Luhn mod-32 check character, always drawn from the safe alphabet. */
function computeCheckChar(body: string): string {
  const n = SAFE_ALPHABET.length;
  let sum = 0;
  // Walk right to left. The rightmost body character is doubled because the
  // check character that follows it takes the undoubled slot.
  let doubled = true;
  for (let i = body.length - 1; i >= 0; i--) {
    let addend = crockfordValue(body[i]);
    if (doubled) {
      addend *= 2;
      addend = Math.floor(addend / n) + (addend % n);
    }
    sum += addend;
    doubled = !doubled;
  }
  return SAFE_ALPHABET[(n - (sum % n)) % n];
}

/** Crockford's mod-37 check symbol, kept only to validate older refs. */
function computeLegacyCheckSymbol(body: string): string {
  let value = 0n;
  for (const char of body) {
    value = value * 32n + BigInt(crockfordValue(char));
  }
  return LEGACY_CHECK_ALPHABET[Number(value % 37n)];
}

function randomSafeChar(): string {
  // crypto.getRandomValues is available in both the Node and browser
  // runtimes Next.js targets, so this stays isomorphic even though only
  // server code calls it today.
  const bytes = new Uint8Array(1);
  crypto.getRandomValues(bytes);
  return SAFE_ALPHABET[bytes[0] % SAFE_ALPHABET.length];
}

/** Generates a new-format tracking ref: PREFIX + 6 random chars + 1 check char. */
export function generatePrefixedTrackingRef(prefix: string): string {
  let body = "";
  for (let i = 0; i < BODY_LENGTH; i++) {
    body += randomSafeChar();
  }
  return `${prefix}${body}${computeCheckChar(body)}`;
}

/**
 * Reads a tracking ref out of a dynamic route segment. Vercel hands the page
 * the segment percent-encoded (ELKREA93N$ arrives as ELKREA93N%24) while the
 * local dev server hands it decoded, so decode before any lookup.
 */
export function trackingRefFromRouteParam(param: string): string {
  try {
    return decodeURIComponent(param);
  } catch {
    return param;
  }
}

/**
 * Normalizes user-typed input before lookup: uppercases, strips whitespace
 * and hyphens, and maps the classic handwriting confusions (O→0, I/L→1) —
 * applied only after the known prefix, since the prefix itself ("ELK") can
 * legitimately contain letters (L) that must never be touched. Safe to
 * apply to legacy hex refs too — hex never contains O, I, or L, so there's
 * nothing in a legacy suffix for this to corrupt.
 */
export function normalizeTrackingRefInput(raw: string, prefix: string): string {
  const cleaned = raw.toUpperCase().replace(/[\s-]/g, "");
  const upperPrefix = prefix.toUpperCase();
  if (!cleaned.startsWith(upperPrefix)) {
    return cleaned;
  }
  const suffix = cleaned
    .slice(upperPrefix.length)
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");
  return `${upperPrefix}${suffix}`;
}

/**
 * Splits a ref into its prefix and suffix given a known prefix, for display
 * chunking and checksum validation.
 */
function splitRef(ref: string, prefix: string): string | null {
  if (!ref.startsWith(prefix)) {
    return null;
  }
  return ref.slice(prefix.length);
}

/** True for new-format refs (7-char suffix); legacy 8-char hex refs are unchecked. */
export function isNewFormatTrackingRef(ref: string, prefix: string): boolean {
  const suffix = splitRef(ref, prefix);
  return suffix != null && suffix.length === BODY_LENGTH + 1;
}

/**
 * Validates the check character on a new-format ref, accepting either the
 * current Luhn check or the mod-37 symbol older refs were issued with.
 * Legacy hex refs (or anything that isn't new-format-shaped) pass through as
 * valid — there's no checksum to fail.
 */
export function isValidTrackingRefChecksum(
  ref: string,
  prefix: string,
): boolean {
  if (!isNewFormatTrackingRef(ref, prefix)) {
    return true;
  }
  const suffix = splitRef(ref, prefix)!;
  const body = suffix.slice(0, BODY_LENGTH);
  const check = suffix.slice(BODY_LENGTH);
  try {
    return (
      computeCheckChar(body) === check ||
      computeLegacyCheckSymbol(body) === check
    );
  } catch {
    // Body contains a character outside the safe alphabet (e.g. leftover
    // typo normalization couldn't fix) — treat as an invalid checksum.
    return false;
  }
}

/** Chunks a new-format ref for display, e.g. ELK4F7KM2P9 -> ELK-4F7K-M2P9. */
export function formatTrackingRefForDisplay(
  ref: string,
  prefix: string,
): string {
  if (!isNewFormatTrackingRef(ref, prefix)) {
    return ref;
  }
  const suffix = splitRef(ref, prefix)!;
  return `${prefix}-${suffix.slice(0, 4)}-${suffix.slice(4)}`;
}
